import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom";

import type { RephraseQuestionType } from "@performyard/service-contracts";

import { AIButton } from "../../../../components/AIModules/AIButton/AIButton";

import { useFeatureFlag } from "../../../../hooks/useFeatureFlag";

type AIAssistantPortalProps = {
	anchorElement: Element;
	elementId: string;
};

/**
 * Finds textareas associated with the given anchor element.
 * Scopes the search to the appropriate container (goal-box or question-block).
 */
const findTextAreasForAnchor = (anchorElement: Element, elementId: string): NodeListOf<Element> => {
	// For goal discussions, scope to the specific goal-box to avoid
	// selecting textareas from other goals within the same question block
	const goalBox = anchorElement.closest(".goal-box");
	if (goalBox) {
		return goalBox.querySelectorAll("textarea");
	}

	// For regular questions, scope to the question block container
	const questionBlock = anchorElement.closest(".question-block");
	if (questionBlock) {
		return questionBlock.querySelectorAll("textarea");
	}

	// Fallback to ID-based search
	return document.querySelectorAll(`textarea[id^="textarea-${elementId}"]`);
};

/**
 * Collects text values from a NodeList of textareas.
 */
const collectTextValues = (textAreas: NodeListOf<Element>): string[] => {
	return Array.from(textAreas).map((textArea) => (textArea as HTMLTextAreaElement).value || "");
};

/**
 * Sets textarea value and dispatches native input/change events
 * to ensure all event listeners (including React state updates) are triggered.
 */
const setTextareaValue = (textarea: HTMLTextAreaElement, value: string): void => {
	textarea.value = value;

	// Dispatch input event (bubbles up to trigger event listeners)
	const inputEvent = new Event("input", { bubbles: true });
	textarea.dispatchEvent(inputEvent);

	// Dispatch change event for completeness
	const changeEvent = new Event("change", { bubbles: true });
	textarea.dispatchEvent(changeEvent);
};

/**
 * AIAssistantPortal component
 *
 * Renders an AI assistant button (Rephrase) to the specified anchor element.
 * The component receives the anchor element directly to avoid ID lookup issues
 * when the same goal appears in multiple questions.
 *
 * Features:
 * - Tracks textarea values and syncs button disabled state
 * - Observes DOM changes to handle dynamically added/removed textareas
 * - Supports multiline questions where new textareas are added on Enter
 */
const AIAssistantPortal = ({ anchorElement, elementId }: AIAssistantPortalProps): React.ReactElement | null => {
	const aiEnabled = useFeatureFlag("ff-is-ai-enabled", false);

	// Read data attributes from the anchor element
	const question = anchorElement.getAttribute("data-question") || "";
	const subText = anchorElement.getAttribute("data-subtext") || "";
	const subject = anchorElement.getAttribute("data-subject") || "";
	const author = anchorElement.getAttribute("data-author") || "";
	const goalName = anchorElement.getAttribute("data-goal-name") || "";
	const questionType = anchorElement.getAttribute("data-type") as RephraseQuestionType;

	// Initialize answers state from current textarea values
	const [answers, setAnswers] = useState<string[]>(() => {
		const textareas = findTextAreasForAnchor(anchorElement, elementId);
		return collectTextValues(textareas);
	});

	// Parse context from the first textarea's data-context attribute
	const getContext = (): Record<string, unknown> => {
		const textareas = findTextAreasForAnchor(anchorElement, elementId);
		try {
			return JSON.parse(textareas[0]?.getAttribute("data-context") || "{}");
		} catch (e) {
			console.error("Error parsing context", e);
			return {};
		}
	};

	const context = getContext();

	// Listen for input events on textareas to update state
	// Also observe DOM changes to handle dynamically added/removed textareas
	useEffect(() => {
		const handleInput = (): void => {
			const currentTextAreas = findTextAreasForAnchor(anchorElement, elementId);
			setAnswers(collectTextValues(currentTextAreas));
		};

		const attachListeners = (): NodeListOf<Element> => {
			const currentTextAreas = findTextAreasForAnchor(anchorElement, elementId);
			currentTextAreas.forEach((textarea) => {
				textarea.addEventListener("input", handleInput);
			});
			return currentTextAreas;
		};

		const detachListeners = (textareas: NodeListOf<Element>): void => {
			textareas.forEach((textarea) => {
				textarea.removeEventListener("input", handleInput);
			});
		};

		// Initial attachment
		let currentTextAreas = attachListeners();

		// Observe DOM changes to re-attach listeners when textareas are added/removed
		// This handles multiline questions where pressing Enter adds a new textarea
		const observeContainer = anchorElement.closest(".goal-box") ?? anchorElement.closest(".question-block");

		if (observeContainer) {
			const observer = new MutationObserver(() => {
				// Detach old listeners
				detachListeners(currentTextAreas);
				// Reattach to current textareas
				currentTextAreas = attachListeners();
				// Update state with current values
				handleInput();
			});

			observer.observe(observeContainer, {
				childList: true,
				subtree: true,
			});

			return () => {
				observer.disconnect();
				detachListeners(currentTextAreas);
			};
		}

		return () => {
			detachListeners(currentTextAreas);
		};
	}, [anchorElement, elementId]);

	// Don't render if AI is disabled
	if (!aiEnabled) {
		return null;
	}

	const handleInsert = (newAnswer: string | string[]): void => {
		// Query for textareas dynamically to include any newly added ones
		const currentTextAreas = findTextAreasForAnchor(anchorElement, elementId);

		if (questionType === "multiline") {
			const lines = Array.isArray(newAnswer) ? newAnswer : newAnswer.split("\n");
			lines.forEach((line, index) => {
				const textarea = currentTextAreas[index] as HTMLTextAreaElement;
				if (textarea) {
					setTextareaValue(textarea, line);
				}
			});
		} else {
			const textarea = currentTextAreas[0] as HTMLTextAreaElement;
			if (textarea) {
				const value = Array.isArray(newAnswer) ? newAnswer.join("\n") : newAnswer;
				setTextareaValue(textarea, value);
			}
		}

		// Update state after inserting
		setAnswers(collectTextValues(currentTextAreas));
	};

	const content = (
		<div
			style={{
				width: "100%",
				display: "flex",
				justifyContent: "end",
				alignItems: "center",
				paddingTop: 4,
			}}
		>
			<AIButton
				moduleType="rephrase"
				disabled={answers.length === 0 || answers.every((answer) => !answer.trim())}
				question={question}
				subText={subText}
				subject={subject}
				author={author}
				answer={answers}
				questionType={questionType}
				goalName={goalName}
				context={context}
				onInsert={handleInsert}
			/>
		</div>
	);

	// Render the portal to the anchor element
	return ReactDOM.createPortal(content, anchorElement);
};

export default AIAssistantPortal;
